import React, { useEffect, useState, useReducer, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
   Divider, Input, Tabs, Table, Typography, Space, Tooltip, Button, Select, Row, Col, message, Popconfirm,
   Radio, Tag, Drawer, notification, Modal, DatePicker, Upload, Form, Spin, Image
} from 'antd';
import * as AntDIcons from '@ant-design/icons';
import axios from 'axios';
import { Levels } from '../../constants';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';
import moment from 'moment';
import './Profile.scss';
import { LoginCreds } from './LoginCreds';
import { ProfilePictureUpload } from './ProfilePictureUpload';
import { getProfilePictureUrl } from './profilePictureUrl';

const { Text } = Typography;
const { Option } = Select;

export function StudentProfile(props) {
   const [form] = Form.useForm();
   const navigate = useNavigate();
   const subdomain = localStorage.getItem("lms-subdomain");

   const user_id = Utils.getCurrentUserInfo(Enums.UserInfo.id);
   const user_role = Utils.getCurrentUserInfo(Enums.UserInfo.role);

   const [updating, setUpdating] = useState(false);
   const [pictPath, setPictPath] = useState(null);
   const [pictureVersion, setPictureVersion] = useState(null);
   const [picturePreview, setPicturePreview] = useState(null);
   const [sectionLoading, setSectionLoading] = useState(false);
   const [sectionList, setSectionList] = useState([]);
   const [studentData, setStudentData] = useState(null);
   const [showCredsModal, setShowCredsModal] = useState(false);
   const [credType, setCredType] = useState(null);
   const [editingContactInfo, setEditingContactInfo] = useState(false);
   const [savingContactInfo, setSavingContactInfo] = useState(false);

   const dateFormat = 'MMM. D, YYYY';
   const customFormat = value => `${value.format(dateFormat)}`;

   useEffect(() => {
      Utils.verifyJwt();

      GetStudentInfo(props.studentID);
   }, [props.studentID]);

   const GetStudentInfo = (id) => {
      axios.get(`${process.env.REACT_APP_API_MANAGEMENT}/getstudentinfo?id=${id}`).then((response) => {
         if (response.data) {
            const student_data = response.data;

            axios.get(`${process.env.REACT_APP_API_LOOKUP}/sectionlistbylevel?level=${student_data.grade_level}`).then((response) => {
               if (response.data) {
                  const data = response.data;

                  var sectionlist = [];
                  data.map((item) => {
                     let obj = { 'value': item.value, 'name': item.name }
                     sectionlist.push(obj);
                  });

                  setSectionList(sectionlist);
                  setSectionLoading(false);

                  setStudentData(student_data);
                  form.setFieldsValue({
                     email: student_data.email !== null ? student_data.email : '',
                     mobile_number: student_data.mobile_number !== null ? student_data.mobile_number : '',
                  });
                  setEditingContactInfo(false);
                  setSavingContactInfo(false);
               }
            }, (error) => {
               setSectionLoading(false);
               console.log(error.message);
            });
         }
      }, (error) => {
         console.log(error.message);
      });
   }

   const handleCancelLoginCreds = () => {
      setShowCredsModal(false);
   }

   const handleChangeCreds = (credtype) => {
      setCredType(credtype);
      setShowCredsModal(true);
   }

   const handleUpdating = () => {
      setUpdating(true);
   }

   const handleSaveLoginCreds = () => {
      setUpdating(false);
      setShowCredsModal(false);
   }

   const handleEditContactInfo = () => {
      form.setFieldsValue({
         email: studentData.email !== null ? studentData.email : '',
         mobile_number: studentData.mobile_number !== null ? studentData.mobile_number : '',
      });
      setEditingContactInfo(true);
   }

   const handleCancelContactInfo = () => {
      form.setFieldsValue({
         email: studentData.email !== null ? studentData.email : '',
         mobile_number: studentData.mobile_number !== null ? studentData.mobile_number : '',
      });
      setEditingContactInfo(false);
   }

   const handleSaveContactInfo = () => {
      form.validateFields(['email', 'mobile_number']).then((values) => {
         const contactInfo = {
            email: values.email !== undefined && values.email !== null ? values.email.trim() : '',
            mobile_number: values.mobile_number !== undefined && values.mobile_number !== null ? values.mobile_number.trim() : '',
         };

         const currentContactInfo = {
            email: studentData.email !== null ? studentData.email : '',
            mobile_number: studentData.mobile_number !== null ? studentData.mobile_number : '',
         };

         if (contactInfo.email === currentContactInfo.email && contactInfo.mobile_number === currentContactInfo.mobile_number) {
            message.success({ content: 'No changes has been made.', key: 'contact-info', duration: 2 });
            setEditingContactInfo(false);
            return;
         }

         const dateOfBirth = studentData.date_of_birth === null || studentData.date_of_birth === '0000-00-00'
            ? ''
            : moment(studentData.date_of_birth).format('YYYY-MM-DD');

         const StudentInfo = {
            id: studentData.id,
            first_name: studentData.first_name,
            last_name: studentData.last_name,
            middle_name: studentData.middle_name,
            gender: studentData.gender,
            date_of_birth: dateOfBirth,
            email: contactInfo.email,
            id_number: studentData.id_number,
            lrn_number: studentData.lrn_number,
            address: studentData.address,
            mobile_number: contactInfo.mobile_number,
            grade_level: studentData.grade_level,
            section: studentData.section,
            picture: pictPath === null ? studentData.picture : pictPath,
            is_active: studentData.is_active,
            zoom_email: studentData.zoom_email,
         };

         setSavingContactInfo(true);

         axios.post(`${process.env.REACT_APP_API_MANAGEMENT}/updatestudentinfo`, StudentInfo).then(response => {
            console.log(response);

            setStudentData({
               ...studentData,
               email: contactInfo.email,
               mobile_number: contactInfo.mobile_number,
            });
            form.setFieldsValue(contactInfo);
            setEditingContactInfo(false);
            setSavingContactInfo(false);

            message.success({ content: 'Contact information updated.', key: 'contact-info', duration: 2 });
         }, (error) => {
            console.log(error.message);
            setSavingContactInfo(false);

            Modal.error({
               title: 'Student Profile',
               content: error.response !== undefined ? error.response.data : 'Unable to update contact information.',
               centered: true,
            });
         });
      }).catch((error) => {
         console.log('Validation Error:', error);
      });
   }

   const handlePictureUploaded = async (key, previewUrl) => {
      const dateOfBirth = studentData.date_of_birth === null || studentData.date_of_birth === '0000-00-00'
         ? ''
         : moment(studentData.date_of_birth).format('YYYY-MM-DD');
      const studentInfo = {
         id: studentData.id,
         first_name: studentData.first_name,
         last_name: studentData.last_name,
         middle_name: studentData.middle_name,
         gender: studentData.gender,
         date_of_birth: dateOfBirth,
         email: studentData.email,
         id_number: studentData.id_number,
         lrn_number: studentData.lrn_number,
         address: studentData.address,
         mobile_number: studentData.mobile_number,
         grade_level: studentData.grade_level,
         section: studentData.section,
         picture: key,
         is_active: studentData.is_active,
         zoom_email: studentData.zoom_email,
      };

      await axios.post(`${process.env.REACT_APP_API_MANAGEMENT}/updatestudentinfo`, studentInfo);
      setPictPath(key);
      setPictureVersion(Date.now());
      setPicturePreview(previewUrl);
      setStudentData({ ...studentData, picture: key });
      props.onPictureUploaded?.(key, previewUrl);
   };

   return (
      <>
         {studentData !== null
            ?
            <Form
               form={form}
               layout={"vertical"}
               colon={false}
            >
               <Row gutter={12}>
                  <Col xs={24} md={5} lg={5}>
                     <Form.Item name="profile_pict" label="Picture">
                        <Space direction='vertical' size={'large'} style={{ width: '100%' }}>
                           <div className='profile_picture'>
                              <Image
                                 style={{ maxHeight: '237px' }}
                                 src={picturePreview || getProfilePictureUrl(
                                    pictPath || `userprofiles/${props.studentID}/${props.studentID}.png`,
                                    subdomain,
                                    pictureVersion
                                 )}
                                 fallback="data:image/png;base64,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"
                              />
                           </div>

                           <ProfilePictureUpload
                              userId={props.studentID}
                              onUploaded={handlePictureUploaded}
                           />
                        </Space>
                     </Form.Item>
                  </Col>
                  <Col xs={24} md={19} lg={19}>
                     <Row gutter={12}>
                        <Col xs={24} md={8} lg={8}>
                           <Form.Item
                              name="first_name"
                              label="First Name"
                              initialValue={studentData.first_name}
                           >
                              <Input
                                 disabled
                                 size='middle'
                                 style={{ borderRadius: 7 }}
                                 value={studentData.first_name}
                              />
                           </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={8}>
                           <Form.Item
                              name="middle_name"
                              label="Middle Name"
                              // rules={[{
                              //    required: true,
                              //    message: 'Middle Name should not be blank'
                              // }]}
                              initialValue={studentData.middle_name}
                           >
                              <Input
                                 disabled
                                 size='middle'
                                 style={{ borderRadius: 7 }}
                                 value={studentData.middle_name}
                              />
                           </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={8}>
                           <Form.Item
                              name="last_name"
                              label="Last Name"
                              initialValue={studentData.last_name}
                           >
                              <Input
                                 disabled
                                 size='middle'
                                 style={{ borderRadius: 7 }}
                                 value={studentData.last_name}
                              />
                           </Form.Item>
                        </Col>

                     </Row>

                     <Row gutter={12}>
                        <Col xs={24} md={8} lg={8}>
                           <Form.Item
                              name="id_number"
                              label="ID Number"
                              // rules={[{
                              //    required: true,
                              //    message: 'Field required'
                              // }]}
                              initialValue={studentData.id_number}
                           >
                              <Input
                                 disabled
                                 size='middle'
                                 style={{ borderRadius: 7 }}
                                 value={studentData.id_number}
                              />
                           </Form.Item>
                        </Col>


                        <Col xs={24} md={8} lg={8}>
                           <Form.Item
                              name="level"
                              label="Level"
                              // rules={[{
                              //    required: true,
                              //    message: 'Field required'
                              // }]}
                              initialValue={studentData.grade_level}
                           >
                              <Select
                                 disabled
                                 size='middle'
                                 style={{ width: '100%' }}
                                 value={studentData.grade_level}
                              >
                                 {Levels.map(({ value, name }, idx) => {
                                    return <Option value={value}>{name}</Option>
                                 })}
                              </Select>
                           </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={8}>
                           <Form.Item
                              name="section"
                              label="Section"
                              initialValue={studentData.section !== '' ? studentData.section : ''}
                           >
                              <Select
                                 disabled
                                 loading={sectionLoading}
                                 size='middle'
                                 style={{ width: '100%' }}
                                 value={studentData.section}
                              >
                                 {sectionList.map(({ value, name }, idx) => {
                                    return <Option value={value}>{name}</Option>
                                 })}
                              </Select>
                           </Form.Item>
                        </Col>
                     </Row>

                     <Row gutter={12}>
                        <Col xs={24} md={5} lg={5}>
                           <Form.Item
                              name="gender"
                              label="Gender"
                              initialValue={studentData.gender}
                           >
                              <Select
                                 disabled
                                 size='middle'
                                 style={{ width: '100%' }}
                                 value={studentData.gender}
                              >
                                 <Option value=''></Option>
                                 <Option value={'male'}>Male</Option>
                                 <Option value={'female'}>Female</Option>
                              </Select>
                           </Form.Item>
                        </Col>
                        <Col xs={24} md={9} lg={9}>
                           <Form.Item
                              name="date_of_birth"
                              label="Date of Birth"
                              initialValue={studentData.date_of_birth !== null && studentData.date_of_birth !== '0000-00-00' ? moment(studentData.date_of_birth) : ''}
                           >
                              <DatePicker
                                 disabled
                                 style={{ width: "100%" }}
                                 format={customFormat}
                                 value={moment(studentData.date_of_birth)}
                              />
                           </Form.Item>
                        </Col>

                        <Col xs={24} md={10} lg={10}>
                           <Form.Item
                              name="lrn_number"
                              label={<Text ellipsis >LRN (Learner Reference Number)</Text>}
                              // rules={[{
                              //    required: true,
                              //    message: 'LRN number should not be blank'
                              // }]}
                              initialValue={studentData.lrn_number}
                           >
                              <Input
                                 disabled
                                 size='middle'
                                 style={{ borderRadius: 7 }}
                                 value={studentData.lrn_number}
                              />
                           </Form.Item>
                        </Col>

                     </Row>

                     <Row gutter={12}>
                        <Col xs={24} md={10} lg={10}>
                           <Form.Item
                              name="email"
                              label="Email Address"
                              rules={[{
                                 type: 'email',
                                 message: 'Please enter a valid email address'
                              }]}
                              initialValue={studentData.email}
                           >
                              <Input
                                 disabled={!editingContactInfo}
                                 size='middle'
                                 style={{ borderRadius: 7 }}
                              />
                           </Form.Item>
                        </Col>

                        <Col xs={24} md={10} lg={10}>
                           <Form.Item
                              name="mobile_number"
                              label="Mobile Number"
                              rules={[{
                                 pattern: /^[0-9\-+()\s]*$/,
                                 message: 'Please enter a valid mobile number'
                              }]}
                              initialValue={studentData.mobile_number}
                           >
                              <Input
                                 disabled={!editingContactInfo}
                                 size='middle'
                                 style={{ borderRadius: 7 }}
                                 maxLength={20}
                              />
                           </Form.Item>
                        </Col>

                        <Col xs={24} md={4} lg={4}>
                           <Form.Item
                              name="is_active"
                              label={<Text ellipsis >Account Active</Text>}
                              initialValue={studentData.is_active}
                           >
                              <Select
                                 disabled
                                 size='middle'
                                 style={{ width: '100%' }}
                                 value={studentData.is_active}
                              >
                                 {/* <Option value=''></Option> */}
                                 <Option value={'yes'}>Yes</Option>
                                 <Option value={'no'}>No</Option>
                              </Select>
                           </Form.Item>
                        </Col>
                     </Row>
                  </Col>
               </Row>

               <Row gutter={12}>
                  <Col xs={24} md={12} lg={12} style={{ textAlign: "left" }}>
                     <Space>
                        {
                           props.canChangeUsername &&
                           <Button type='primary' shape='round' className='button-shadow' onClick={() => handleChangeCreds('username')}>Change Username</Button>
                        }
                        {
                           props.canChangePassword &&
                           <Button type='primary' shape='round' className='button-shadow' onClick={() => handleChangeCreds('password')}>Change Password</Button>
                        }
                     </Space>
                  </Col>

                  <Col xs={24} md={12} lg={12} style={{ textAlign: "right" }}>
                     <Space direction='horizontal' size={'small'}>
                        {
                           editingContactInfo
                              ?
                              <>
                                 <Button shape='round' onClick={handleCancelContactInfo} disabled={savingContactInfo}>Cancel</Button>
                                 <Button shape='round' type='primary' className='button-shadow' loading={savingContactInfo} onClick={handleSaveContactInfo}>Save Contact Info</Button>
                              </>
                              :
                              <Button shape='round' type='primary' className='button-shadow' onClick={handleEditContactInfo}>Edit Contact Info</Button>
                        }
                        <Button shape='round' className='modal-dismiss-btn' disabled={savingContactInfo} onClick={() => { props.onCancelCallback(); }}>Close</Button>
                     </Space>
                  </Col>
               </Row>
            </Form >
            :
            <div className='temp-answer-sheet-content2' style={{ height: "480px" }}>
               <Spin size='large' tip="Loading, please wait..." />
            </div>
         }

         <Modal
            className="profile-modal profile-credentials-modal"
            title={"Change Login Credentials"}
            centered
            destroyOnClose={true}
            onCancel={() => { setShowCredsModal(false); }}
            open={showCredsModal}
            // width={window.innerWidth - 400}
            width='35%'
            // footer={null}
            style={{ minWidth: '350px' }}
            okButtonProps={{ style: { display: 'none' } }}
            cancelButtonProps={{ style: { display: 'none' } }}
         >
            <Spin spinning={updating} tip={"Processing, please wait..."}>
               <LoginCreds
                  userid={props.studentID}
                  credtype={credType}
                  onUpdatingCallBack={handleUpdating}
                  onSaveCompleteCallBack={handleSaveLoginCreds}
                  onCancelCallback={handleCancelLoginCreds}
               />
            </Spin>
         </Modal>
      </>
   );
}
